/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Spinner, SpinnerProps } from "react-bootstrap";
import SvgIcon, { SvgIconProps } from "./SvgIcon";

const defaultLoadingProps = (): SpinnerProps => ({
  animation: "border",
  size: "sm",
  variant: "secondary"
});

const defaultClassName = "button";

export type ButtonProps = React.ComponentPropsWithRef<"button"> & {
  title?: string | React.ReactElement<unknown>;
  icon: string | React.ReactElement<SvgIconProps>;
  iconProps?: SvgIconProps | null;
  loading?: boolean;
  loadingProps?: SpinnerProps;
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      loading,
      loadingProps,
      disabled,
      icon,
      iconProps,
      className,
      title,
      onClick = () => console.log("Please attach a method to this button"),
      children,
      ...rest
    },
    ref
  ) => {
    const handleOnClick = React.useCallback(
      (event) => {
        if (!loading && !disabled) {
          onClick(event);
        }
      },
      [loading, onClick, disabled]
    );

    const renderIcon = (icon, iconProps) => {
      if (typeof icon == "string") {
        return <SvgIcon name={icon} className={title && "mr-3"} {...iconProps} />;
      } else {
        return icon;
      }
    };

    const renderTitle = (title) => {
      if (typeof title == "string") {
        return <span>{title}</span>;
      } else {
        return title;
      }
    };

    return (
      <button ref={ref} type="button" className={`${defaultClassName} ${className}`} disabled={disabled} onClick={handleOnClick} {...rest}>
        {loading && <Spinner className="mr-3" {...defaultLoadingProps()} {...loadingProps} />}
        {!loading && icon && renderIcon(icon, iconProps)}
        {title && renderTitle(title)}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
